import { excludeIntersection } from 'utils/array/exclude-intersection';
import { highlightsHaveSameKeyAndColor } from 'chessboard/plugins/highlights/service/highlights-have-same-key-and-color';
import type {
  KeyedHighlight,
  HighlightsModule,
  HighlightChanges,
  API,
} from 'chessboard/plugins/highlights/config/types';

/**
 * Create a service to manage highlights.
 */
export function createHighlights<T extends any[]>(initialModules: HighlightsModule<T>[]): API<T> {
  const modules = new Set(initialModules);
  const highlights = new Map<string, KeyedHighlight>();

  return {
    ...modules,
    addModule,
    getModules: getAllModulesAsArray,
    removeModule,
    getAll,
    update,
    clearState,
  };

  /**
   * Clears highlights plugin internal state, but does not modify board.
   * Useful when refreshing highlights after something like a renderer update.
   */
  function clearState() {
    highlights.clear();
  }

  /**
   * Add a module.
   */
  function addModule(mod: HighlightsModule<T>) {
    return modules.add(mod);
  }

  /**
   * Get all highlights as an array
   */
  function getAll(): KeyedHighlight[] {
    return Array.from(highlights.values());
  }

  /**
   * Get all the modules as an array.
   */
  function getAllModulesAsArray(): HighlightsModule<T>[] {
    return Array.from(modules);
  }

  /**
   * Remove a module.
   */
  function removeModule(mod: HighlightsModule<T>) {
    return modules.delete(mod);
  }

  /**
   * Get changes to the highlights as defined by the priority of the highlights modules.
   */
  function update(...params: T): HighlightChanges {
    const toRemove = getAll();

    // First, clear the highlights
    clearState();

    // Then get those highlights we need to add, overwriting with each subsequent module (higher priority)
    const toAdd = Array.from(
      getAllModulesAsArray()
        .reduce((acc, mod) => {
          mod(...params).forEach((highlight) => {
            acc.set(highlight.data.square, highlight);
          });

          return acc;
        }, new Map<string, KeyedHighlight>())
        .values(),
    );

    // Add the new highlights into the map
    toAdd.forEach((highlight) => highlights.set(highlight.data.square, highlight));

    // Exclude the intersection from both arrays because those are the unchanged highlights.
    const toAddWithoutRemoved = excludeIntersection(toAdd, toRemove, {
      isDuplicateWhen: highlightsHaveSameKeyAndColor,
    });

    const toRemoveWithoutAdded = excludeIntersection(toRemove, toAdd, {
      isDuplicateWhen: highlightsHaveSameKeyAndColor,
    });

    return {
      toAdd: toAddWithoutRemoved,
      toRemove: toRemoveWithoutAdded,
    };
  }
}
